import React from 'react';
import { Calendar, MapPin } from 'lucide-react';
import type { ApiInvitation, TemplateElement, WeddingPersonDetails } from '@/src/types';
import { injectValue, isTruthyCondition } from './data-injector';
import { safeImageUrl, safeLinkUrl } from './url-safety';
import { useTemplateContext } from './template-context';

type ComponentProps = {
  node: TemplateElement;
  children?: React.ReactNode;
};

type RegistryComponent = React.ComponentType<ComponentProps>;

class ComponentRegistry {
  private readonly components = new Map<string, RegistryComponent>();

  register(type: string, component: RegistryComponent) {
    this.components.set(type, component);
  }

  resolve(type: string) {
    return this.components.get(type) ?? null;
  }
}

export const componentRegistry = new ComponentRegistry();

function textClass(variant: unknown) {
  switch (variant) {
    case 'display':
      return 'text-5xl sm:text-6xl font-semibold leading-tight';
    case 'heading':
      return 'text-4xl sm:text-5xl font-semibold leading-tight';
    case 'sectionHeading':
      return 'text-xl font-semibold uppercase tracking-[0.18em]';
    case 'label':
      return 'text-xs font-bold tracking-[0.26em]';
    case 'caption':
      return 'text-sm font-medium';
    default:
      return 'text-base leading-7';
  }
}

function TextElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const content = injectValue(node.content, data);
  const variant = node.variant;
  const fontFamily = variant === 'display' ? tokens.font.serif : tokens.font.sans;

  return (
    <p
      className={textClass(variant)}
      style={{
        color: variant === 'label' || variant === 'caption' ? tokens.color.textMuted : tokens.color.text,
        fontFamily,
        textTransform: node.transform === 'uppercase' ? 'uppercase' : undefined,
      }}
    >
      {String(content ?? '')}
    </p>
  );
}

function ImageElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const src = safeImageUrl(injectValue(node.src, data));
  if (!src) return null;

  return (
    <img
      src={src}
      alt=""
      className="w-full object-cover"
      style={{
        aspectRatio: String(node.aspectRatio ?? '16/9'),
        borderRadius: tokens.radius.md,
      }}
    />
  );
}

function DividerElement({ node }: ComponentProps) {
  const { tokens } = useTemplateContext();
  if (node.variant === 'ornamental') {
    return (
      <div className="flex items-center gap-4 py-3">
        <div className="h-px flex-1" style={{ backgroundColor: tokens.color.border }} />
        <span className="text-sm" style={{ color: tokens.color.secondary }}>◆</span>
        <div className="h-px flex-1" style={{ backgroundColor: tokens.color.border }} />
      </div>
    );
  }

  return <div className="h-px w-full my-4" style={{ backgroundColor: tokens.color.border }} />;
}

function DetailRowElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const value = injectValue(node.value, data);
  const Icon = node.icon === 'map-pin' ? MapPin : Calendar;

  return (
    <div className="flex gap-4 rounded-xl p-4" style={{ backgroundColor: `${tokens.color.accent}33` }}>
      <Icon size={22} style={{ color: tokens.color.primary }} />
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: tokens.color.textMuted }}>
          {String(node.label ?? '')}
        </p>
        <p className="text-base font-semibold mt-1" style={{ color: tokens.color.text }}>
          {String(value ?? '')}
        </p>
      </div>
    </div>
  );
}

function BlockquoteElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const content = injectValue(node.content, data);

  return (
    <blockquote
      className="text-lg leading-8 italic text-center px-4 py-5"
      style={{ color: tokens.color.text, borderColor: tokens.color.border }}
    >
      "{String(content ?? '')}"
    </blockquote>
  );
}

function GalleryElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const raw = injectValue(node.images, data);
  const images = Array.isArray(raw)
    ? raw
        .map((item) => safeImageUrl(item))
        .filter((item): item is string => item !== null)
    : [];
  const start = node.skipFirst ? 1 : 0;
  const max = typeof node.maxItems === 'number' ? node.maxItems : 6;
  const visible = images.slice(start, start + max);
  if (visible.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-2">
      {visible.map((url) => (
        <img
          key={url}
          src={url}
          alt=""
          className="h-36 w-full object-cover"
          style={{ borderRadius: tokens.radius.md }}
        />
      ))}
    </div>
  );
}

function ButtonElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  if (!isTruthyCondition(node.condition, data)) return null;
  const href = safeLinkUrl(injectValue(node.href, data));
  if (!href) return null;

  return (
    <a
      href={href}
      target={href.startsWith('#') ? undefined : '_blank'}
      rel={href.startsWith('#') ? undefined : 'noopener noreferrer'}
      className="inline-flex items-center justify-center px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] transition-transform hover:-translate-y-0.5"
      style={{
        backgroundColor: tokens.color.primary,
        color: tokens.color.surface,
        borderRadius: tokens.radius.full,
      }}
    >
      {String(node.label ?? 'Open')}
    </a>
  );
}

function RsvpFormElement({ node }: ComponentProps) {
  const { data, tokens, mode } = useTemplateContext();
  const invitationId = injectValue(node.invitationId, data);

  return (
    <div className="rounded-2xl border p-5 text-center" style={{ borderColor: tokens.color.border }}>
      <p className="text-sm font-semibold" style={{ color: tokens.color.text }}>
        RSVP for {data.eventName}
      </p>
      <p className="mt-1 text-xs" style={{ color: tokens.color.textMuted }}>
        {mode === 'preview'
          ? 'Guests will see the RSVP form here.'
          : `Invitation reference: ${String(invitationId ?? data.id)}`}
      </p>
    </div>
  );
}

// ── Lagna Patrika (Marathi wedding) elements ─────────────────────────────────
// These render the seeded "Lagna Patrika" templates (blessing / names / muhurta
// / venues / ceremonies sections). They read from the invitation data where it
// exists and degrade to the node's own literal props otherwise, so a preview
// looks complete even before the couple fills anything in. None of them ever
// throw or surface an error to the guest.

const MOTIF_GLYPHS: Record<string, string> = {
  'ganesh-classic': 'ॐ',
  kalash: '\u{1FA94}', // diya lamp
  'mandap-arch': '⛩', // shinto shrine ~ arch
  diya: '\u{1FA94}',
};

function ToneBadgeElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const label = injectValue(node.label ?? node.content, data);
  const text = typeof label === 'string' && label.trim() ? label : 'शुभमंगल';

  return (
    <span
      className="inline-block rounded-full px-4 py-1 text-xs font-semibold tracking-[0.2em]"
      style={{
        backgroundColor: `${tokens.color.secondary}22`,
        color: tokens.color.secondary,
        border: `1px solid ${tokens.color.secondary}55`,
      }}
    >
      {String(text)}
    </span>
  );
}

function SvgMotifElement({ node }: ComponentProps) {
  const { tokens } = useTemplateContext();
  const motif = typeof node.motif === 'string' ? node.motif : '';
  const glyph = MOTIF_GLYPHS[motif] ?? '✨';
  const size = typeof node.size === 'number' ? node.size : 80;
  const color = typeof node.color === 'string' ? node.color : tokens.color.secondary;

  return (
    <div
      aria-hidden
      className="mx-auto flex items-center justify-center leading-none select-none"
      style={{ fontSize: Math.round(size * 0.6), color }}
    >
      {glyph}
    </div>
  );
}

// ── Mandala divider ──────────────────────────────────────────────────────────
// A decorative, purely presentational SVG used by the Lagna Patrika "Nu-Marathi" templates
// (`{ type: 'mandala', rings, petals, color, size, opacity }`). Every value comes from template JSON,
// so each is bounded or validated: a bad value falls back to a default and can never throw, crash the
// page, or inject CSS. Static (no animation), hidden from assistive tech.

const SAFE_HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const MANDALA_FALLBACK_COLOR = '#D4AF37';

function boundedNumber(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
}

function MandalaElement({ node }: ComponentProps) {
  const { tokens } = useTemplateContext();
  const rings = Math.round(boundedNumber(node.rings, 1, 6, 2));
  const petals = Math.round(boundedNumber(node.petals, 4, 24, 8));
  const size = Math.round(boundedNumber(node.size, 24, 320, 80));
  const opacity = boundedNumber(node.opacity, 0, 1, 0.5);
  // Template value first, then the theme colour — both validated, since both come from stored JSON —
  // and finally a literal, so nothing unvalidated ever reaches an SVG paint attribute.
  const pickColor = (...candidates: unknown[]): string => {
    for (const candidate of candidates) {
      const value = typeof candidate === 'string' ? candidate.trim() : '';
      if (SAFE_HEX_COLOR.test(value)) return value;
    }
    return MANDALA_FALLBACK_COLOR;
  };
  const color = pickColor(node.color, tokens.color.secondary);

  const petalShapes: React.ReactNode[] = [];
  for (let ring = 0; ring < rings; ring += 1) {
    const length = 10 + ((ring + 1) * 34) / rings; // outermost petal tip stays inside the r=47 frame
    const halfWidth = Math.min(7, Math.max(2, (2 * Math.PI * length * 0.55) / petals / 2));
    const offset = ring % 2 === 1 ? 180 / petals : 0; // stagger alternate rings between each other
    for (let petal = 0; petal < petals; petal += 1) {
      const angle = (petal * 360) / petals + offset;
      petalShapes.push(
        <ellipse
          key={`${ring}-${petal}`}
          cx={50}
          cy={(50 - length / 2).toFixed(2)}
          rx={halfWidth.toFixed(2)}
          ry={(length / 2).toFixed(2)}
          transform={`rotate(${angle.toFixed(2)} 50 50)`}
          fill={color}
          fillOpacity={0.06}
        />,
      );
    }
  }

  return (
    <div aria-hidden data-element-type="mandala" className="mx-auto flex justify-center py-2" style={{ opacity }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        stroke={color}
        strokeWidth={0.8}
        role="presentation"
        focusable="false"
        style={{ maxWidth: '100%', height: 'auto' }} // never overflow a narrow card
      >
        <circle cx={50} cy={50} r={47} />
        {petalShapes}
        <circle cx={50} cy={50} r={2.5} fill={color} stroke="none" />
      </svg>
    </div>
  );
}

function readPerson(data: ApiInvitation, ref: unknown): WeddingPersonDetails | null {
  if (ref !== 'groom' && ref !== 'bride') return null;
  return data.customData?.wedding?.[ref] ?? null;
}

function PersonBlockElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const person = readPerson(data, node.personRef);
  const name = person?.fullName ?? (node.personRef === 'bride' ? 'Bride' : 'Groom');
  const parents =
    person?.fatherName && person?.motherName
      ? `${person.motherName} & ${person.fatherName}`
      : null;

  return (
    <div className="text-center space-y-1">
      <p
        className="text-3xl"
        style={{ fontFamily: tokens.font.serif, color: tokens.color.primary }}
      >
        {name}
      </p>
      {person?.profession ? (
        <p className="text-sm" style={{ color: tokens.color.textMuted }}>
          {person.profession}
        </p>
      ) : null}
      {parents ? (
        <p className="text-xs" style={{ color: tokens.color.textMuted }}>
          {parents}
        </p>
      ) : null}
    </div>
  );
}

function DecorativeHeadlineElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const text = injectValue(node.text ?? node.content, data);
  const color = typeof node.color === 'string' ? node.color : tokens.color.primary;

  return (
    <div className="flex items-center justify-center gap-3 py-2">
      <span style={{ color: tokens.color.secondary }}>❖</span>
      <h2
        className="text-3xl sm:text-4xl font-semibold text-center"
        style={{ color, fontFamily: tokens.font.serif }}
      >
        {String(text ?? '')}
      </h2>
      <span style={{ color: tokens.color.secondary }}>❖</span>
    </div>
  );
}

function MuhurtaBlockElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const date = data.eventDate ? new Date(data.eventDate) : null;
  const formatted =
    date && !Number.isNaN(date.getTime())
      ? new Intl.DateTimeFormat('en-IN', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(date)
      : '';

  return (
    <div
      className="mx-auto max-w-sm rounded-2xl px-6 py-5 text-center"
      style={{ backgroundColor: `${tokens.color.accent}22`, border: `1px solid ${tokens.color.border}` }}
    >
      {node.showMuhurtaLabel ? (
        <p className="text-xs font-bold uppercase tracking-[0.22em]" style={{ color: tokens.color.secondary }}>
          शुभ मुहूर्त
        </p>
      ) : null}
      <p className="mt-2 text-lg font-semibold" style={{ color: tokens.color.text }}>
        {formatted}
      </p>
    </div>
  );
}

function VenueBlockElement({ node }: ComponentProps) {
  const { data, tokens } = useTemplateContext();
  const isReception = node.venueKey === 'reception';
  const venueName = isReception ? null : data.eventVenue;
  if (isReception && !venueName) return null;

  return (
    <div className="text-center space-y-1 py-2">
      <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: tokens.color.secondary }}>
        {isReception ? 'Reception' : 'Wedding Venue'}
      </p>
      <p className="text-base font-semibold" style={{ color: tokens.color.text }}>
        {String(venueName ?? 'Venue to be announced')}
      </p>
    </div>
  );
}

function CeremonyBarElement() {
  const { data, tokens } = useTemplateContext();
  const events = data.customData?.wedding?.weddingEvents ?? [];
  if (events.length === 0) return null;

  return (
    <div className="space-y-2">
      {events.map((ev, idx) => (
        <div
          key={ev.title ?? idx}
          className="flex items-center justify-between rounded-xl px-4 py-3"
          style={{ backgroundColor: `${tokens.color.accent}22` }}
        >
          <span className="font-semibold" style={{ color: tokens.color.text }}>
            {ev.title ?? 'Ceremony'}
          </span>
          <span className="text-sm" style={{ color: tokens.color.textMuted }}>
            {[ev.startTime, ev.venue].filter(Boolean).join(' · ')}
          </span>
        </div>
      ))}
    </div>
  );
}

componentRegistry.register('text', TextElement);
componentRegistry.register('image', ImageElement);
componentRegistry.register('divider', DividerElement);
componentRegistry.register('detail-row', DetailRowElement);
componentRegistry.register('blockquote', BlockquoteElement);
componentRegistry.register('gallery', GalleryElement);
componentRegistry.register('button', ButtonElement);
componentRegistry.register('rsvp-form', RsvpFormElement);
componentRegistry.register('tone-badge', ToneBadgeElement);
componentRegistry.register('svg-motif', SvgMotifElement);
componentRegistry.register('mandala', MandalaElement);
componentRegistry.register('person-block', PersonBlockElement);
componentRegistry.register('decorative-headline', DecorativeHeadlineElement);
componentRegistry.register('muhurta-block', MuhurtaBlockElement);
componentRegistry.register('venue-block', VenueBlockElement);
componentRegistry.register('ceremony-bar', CeremonyBarElement);
